import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LMSHeader from '../components/LMSHeader';
import useCourseStore from '../stores/useCourseStore';
import CourseCard from '../components/CourseCard';
import useAuthStore from '../stores/useAuthStore';

const CategoryCoursePage = () => {
    const user = useAuthStore(state => state.user);
    const { token } = useAuthStore();
    const { categorySlug } = useParams();
    const navigate = useNavigate();
    const {
        courses,
        fetchCourses,
        categories: allCategories,
        isLoading,
        error
    } = useCourseStore();

    const enrolledCourses = useCourseStore(state => state.enrolledCourses);
    const fetchMyCourses = useCourseStore(state => state.fetchMyCourses);

    const [categoryName, setCategoryName] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        sortBy: 'createdAt',
        order: 'desc',
        minPrice: '',
        maxPrice: '',
        minRating: '',
        duration: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCourses: 0
    });

    useEffect(() => {
        if (user?.role === "student") {
            const loadCourseData = async () => { await fetchMyCourses() };
            loadCourseData();
        }
    }, [user, fetchMyCourses]);

    // Find category name
    useEffect(() => {
        if (allCategories.length > 0 && categorySlug) {
            const findCategoryBySlug = (cats, slug) => {
                for (const cat of cats) {
                    if (cat.slug === slug) return cat;
                    if (cat.children) {
                        const found = findCategoryBySlug(cat.children, slug);
                        if (found) return found;
                    }
                }
                return null;
            };
            const cat = findCategoryBySlug(allCategories, categorySlug);
            if (cat) setCategoryName(cat.name);
        }
    }, [allCategories, categorySlug]);

    // Fetch courses
    useEffect(() => {
        const fetchData = async () => {
            const result = await fetchCourses({
                ...filters,
                category: categorySlug,
                page: pagination.currentPage
            });
            if (result?.pagination) {
                setPagination({
                    currentPage: result.pagination.currentPage,
                    totalPages: result.pagination.totalPages,
                    totalCourses: result.pagination.totalCourses
                });
            }
        };
        fetchData();
    }, [filters, pagination.currentPage, categorySlug, fetchCourses]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleEnroll = async (courseSlug, courseId) => {
        if (!token) {
            localStorage.setItem('redirectAfterLogin', `/courses/${courseSlug}/${courseId}`);
            navigate('/login');
            return;
        }
        try {
            const result = await useCourseStore.getState().enrollInCourse(courseId);
            if (result?.data?.authorizationUrl) {
                window.location.href = result.data.authorizationUrl;
            } else {
                alert('Enrolled successfully!');
            }
        } catch (err) {
            console.error('Enrollment failed:', err);
            alert('Failed to enroll in course');
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: page }));
        }
    };

    return (
        <>
            <LMSHeader />
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <button
                            onClick={() => navigate('/categories')}
                            className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Categories
                        </button>
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
                            {categoryName || 'Courses'}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            Browse courses in this category
                        </p>
                    </div>

                    {/* Filters */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Search
                                </label>
                                <input
                                    type="text"
                                    placeholder="Course title, description..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Level
                                </label>
                                <select
                                    value={filters.level}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">All Levels</option>
                                    <option value="Beginner">Beginner</option>
                                    <option value="Intermediate">Intermediate</option>
                                    <option value="Advanced">Advanced</option>
                                    <option value="All Level">All Level</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Sort By
                                </label>
                                <select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="createdAt">Newest</option>
                                    <option value="price">Price: Low to High</option>
                                    <option value="rating">Highest Rated</option>
                                    <option value="enrollmentCount">Most Enrolled</option>
                                </select>
                            </div>
                            <div className="md:col-span-2 lg:col-span-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Price Range (₦)
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={filters.minPrice}
                                        onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={filters.maxPrice}
                                        onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results */}
                    {error && (
                        <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                            <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : courses?.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {courses?.map(course => (
                                    <CourseCard
                                        key={course?._id}
                                        course={course}
                                        onEnroll={() => handleEnroll(course?.slug, course?._id)}
                                        isEnrolled={enrolledCourses.some(c => c?._id === course?._id) ? true : false}
                                    />
                                ))}
                            </div>

                            {pagination?.totalPages > 1 && (
                                <div className="mt-8 flex justify-center">
                                    <nav className="inline-flex rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        {[...Array(pagination.totalPages)].map((_, i) => (
                                            <button
                                                key={i + 1}
                                                onClick={() => handlePageChange(i + 1)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === i + 1
                                                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                                                    : 'bg-white dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                                                    }`}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </nav>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">📚</div>
                            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses found</h3>
                            <p className="text-neutral-600 dark:text-neutral-400">
                                There are no courses in this category yet.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CategoryCoursePage;